import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Maximize2, BookOpen } from "lucide-react";
import { BROCHURE_PAGES, BROCHURE_PDF } from "../data/brochure";
import { Lightbox } from "./Lightbox";

const WIDE = "(min-width: 900px)";
const DESKTOP_SPREADS = [[0], [1, 2], [3, 4], [5, 6]];
const SINGLE_SPREADS = BROCHURE_PAGES.map((_, i) => [i]);

const useWide = () => {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const onChange = () => setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
};

const Thumbnails = ({ spread, onSelect }) => (
  <div className="flipbook-thumbs" data-testid="flipbook-thumbnails">
    {BROCHURE_PAGES.map((p, i) => (
      <button key={p.src} className={`flipbook-thumb ${spread.includes(i) ? "active" : ""}`} onClick={() => onSelect(i)}
        aria-label={`Go to page ${i + 1}`} data-testid={`flipbook-thumb-${i + 1}`}>
        <img src={p.src} alt="" loading="lazy" />
        <span>{i + 1}</span>
      </button>
    ))}
  </div>
);

export const BrochureViewer = ({ innerRef }) => {
  const wide = useWide();
  const spreads = wide ? DESKTOP_SPREADS : SINGLE_SPREADS;
  const [page, setPage] = useState(0);
  const [dir, setDir] = useState("next");
  const [zoom, setZoom] = useState(null);
  const touchX = useRef(null);

  const idx = spreads.findIndex((s) => s.includes(page));
  const spread = spreads[idx];
  const goTo = (target) => { setDir(target > page ? "next" : "prev"); setPage(target); };
  const step = (n) => { const next = spreads[idx + n]; if (next) goTo(next[0]); };
  const label = spread.length > 1 ? `Pages ${spread[0] + 1}–${spread[1] + 1}` : `Page ${spread[0] + 1}`;

  const onKey = (e) => { if (e.key === "ArrowRight") step(1); if (e.key === "ArrowLeft") step(-1); };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
  };

  return (
    <section id="profile" className="flipbook-section section-animate" ref={innerRef} data-testid="brochure-section">
      <div className="section-header">
        <span className="flipbook-eyebrow"><BookOpen /> E-Brochure</span>
        <h2 className="section-title" data-testid="brochure-title">Our Corporate Profile</h2>
        <div className="section-divider"></div>
        <p className="section-subtitle">Flip through our corporate profile, or download it to share with your team.</p>
      </div>

      <div className="flipbook-stage" tabIndex={0} onKeyDown={onKey} onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={onTouchEnd} data-testid="flipbook-stage">
        <button className="flipbook-arrow" onClick={() => step(-1)} disabled={idx === 0} aria-label="Previous page" data-testid="flipbook-prev">
          <ChevronLeft />
        </button>
        <div key={`${wide}-${idx}`} className={`flipbook-spread flip-${dir} ${spread.length > 1 ? "is-spread" : "is-single"}`} data-testid="flipbook-spread">
          {spread.map((i) => (
            <button key={i} className="flipbook-page" onClick={() => setZoom(i)} aria-label={`Enlarge page ${i + 1}`} data-testid={`flipbook-page-${i + 1}`}>
              <img src={BROCHURE_PAGES[i].src} alt={BROCHURE_PAGES[i].alt} />
            </button>
          ))}
        </div>
        <button className="flipbook-arrow" onClick={() => step(1)} disabled={idx === spreads.length - 1} aria-label="Next page" data-testid="flipbook-next">
          <ChevronRight />
        </button>
      </div>

      <div className="flipbook-toolbar">
        <span className="flipbook-counter" data-testid="flipbook-counter">{label} of {BROCHURE_PAGES.length}</span>
        <div className="flipbook-actions">
          <button className="btn-secondary flipbook-btn" onClick={() => setZoom(spread[0])} data-testid="flipbook-fullscreen">
            <Maximize2 className="btn-icon" /> Full Screen
          </button>
          <a className="btn-primary flipbook-btn" href={BROCHURE_PDF} download="Fit Farm Training & Development Corporate Profile.pdf" data-testid="flipbook-download">
            <Download className="btn-icon" /> Download PDF
          </a>
        </div>
      </div>

      <Thumbnails spread={spread} onSelect={goTo} />
      <Lightbox images={BROCHURE_PAGES} index={zoom} onClose={() => setZoom(null)} onChange={setZoom} testId="brochure-lightbox" />
    </section>
  );
};
